import { useState } from 'react';
import { otpAPI } from '../services/api';

const OTPVerification = ({ email, onVerified, onResend }) => {
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);

    const handleVerify = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // DEVELOPMENT MODE: Auto-accept hardcoded OTP
        const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const DEV_OTP = '123456';
        
        if (isDevelopment && otp === DEV_OTP) {
            console.log('🔧 DEVELOPMENT MODE: Using hardcoded OTP');
            try {
                const response = await otpAPI.verify(email, DEV_OTP);
                if (response.success) {
                    onVerified();
                }
            } catch (err) {
                setError(err.message || 'Invalid OTP. Please try again.');
            } finally {
                setLoading(false);
            }
            return;
        }

        try {
            const response = await otpAPI.verify(email, otp);
            if (response.success) {
                onVerified();
            }
        } catch (err) {
            setError(err.message || 'Invalid OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResending(true);
        setError('');
        try {
            await otpAPI.send(email);
            if (onResend) onResend();
        } catch (err) {
            setError(err.message || 'Failed to resend OTP');
        } finally {
            setResending(false);
        }
    };

    const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const DEV_OTP = '123456';

    return (
        <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold mb-4">Verify Your Email</h2>
            {isDevelopment && (
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded mb-4">
                    <p className="text-sm font-semibold">🔧 DEVELOPMENT MODE</p>
                    <p className="text-xs mt-1">Use OTP: <strong>{DEV_OTP}</strong> (or click verify with this code)</p>
                </div>
            )}
            <p className="text-gray-600 mb-6">
                We've sent a 6-digit verification code to <strong>{email}</strong>
            </p>

            <form onSubmit={handleVerify} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium mb-2">Enter OTP</label>
                    <input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder={isDevelopment ? DEV_OTP : "000000"}
                        maxLength={6}
                        required
                    />
                    {isDevelopment && (
                        <p className="text-xs text-gray-500 mt-1">
                            💡 Development tip: Enter <strong>{DEV_OTP}</strong> to auto-verify
                        </p>
                    )}
                </div>

                {error && (
                    <div className="text-red-600 text-sm">{error}</div>
                )}

                <button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                    {loading ? 'Verifying...' : 'Verify Email'}
                </button>
            </form>

            <div className="mt-4 text-center">
                <button
                    onClick={handleResend}
                    disabled={resending}
                    className="text-blue-600 hover:underline disabled:opacity-50"
                >
                    {resending ? 'Sending...' : "Didn't receive code? Resend"}
                </button>
            </div>
        </div>
    );
};

export default OTPVerification;


